import React, { useEffect, useRef, useState } from "react";
import { Search, X, Check } from "lucide-react";
import api from "@/lib/api";
import { titleOf } from "./crudUtils";

const nice = (slug) => String(slug).split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
const labelOf = (entity, row) => {
  if (entity === "locations" && row.state_code && row.level !== "state") return `${row.name}, ${row.state_code}`;
  if (entity === "categories" && row.path?.length) return `${nice(row.path[row.path.length - 1])} › ${row.name}`;
  return titleOf(row);
};

export default function RefPicker({ entity, value, onChange, multi, testid }) {
  const ids = multi ? value || [] : value ? [value] : [];
  const [labels, setLabels] = useState({});
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const box = useRef(null);

  useEffect(() => {
    ids.filter((id) => !labels[id]).forEach((id) => api.get(`/admin/crud/${entity}/${id}`).then(({ data }) => setLabels((l) => ({ ...l, [id]: labelOf(entity, data) }))).catch(() => {}));
    // eslint-disable-next-line
  }, [ids.join(","), entity]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      api.get(`/admin/crud/${entity}`, { params: { q: q || undefined, limit: 8, sort: entity === "locations" ? "name" : "-created_at" } })
        .then(({ data }) => setResults(data.items || [])).catch(() => setResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q, open, entity]);

  useEffect(() => {
    const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const pick = (row) => {
    setLabels((l) => ({ ...l, [row.id]: labelOf(entity, row) }));
    if (multi) onChange(ids.includes(row.id) ? ids.filter((x) => x !== row.id) : [...ids, row.id]);
    else { onChange(row.id); setOpen(false); }
    setQ("");
  };

  return (
    <div className="relative mt-1.5" ref={box}>
      <div className="flex flex-wrap items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-2.5 py-2 focus-within:border-[#165a71] focus-within:ring-4 focus-within:ring-[#165a71]/10 transition-[border-color,box-shadow]">
        {ids.map((id) => (
          <span key={id} className="inline-flex items-center gap-1 rounded-full bg-[#165a71]/10 text-[#165a71] px-2.5 py-0.5 text-xs font-medium">
            {labels[id] || "Loading…"}
            <button type="button" onClick={() => onChange(multi ? ids.filter((x) => x !== id) : null)} aria-label="Remove" data-testid={`${testid}-remove-${id}`}><X className="h-3 w-3" /></button>
          </span>
        ))}
        {(multi || ids.length === 0) && (
          <div className="flex flex-1 min-w-[140px] items-center gap-2 px-1">
            <Search className="h-3.5 w-3.5 text-slate-400" />
            <input value={q} onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }} placeholder="Search…" className="w-full bg-transparent text-sm outline-none" data-testid={testid} />
          </div>
        )}
      </div>
      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden" data-testid={`${testid}-results`}>
          {results.length === 0 && <div className="px-4 py-3 text-sm text-slate-400">No matches</div>}
          {results.map((r) => (
            <button type="button" key={r.id} onClick={() => pick(r)} className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left text-sm hover:bg-slate-50" data-testid={`${testid}-option-${r.id}`}>
              <span className="truncate text-[#071b22]">{labelOf(entity, r)}</span>
              {ids.includes(r.id) && <Check className="h-4 w-4 text-[#165a71]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
